import { apiUrl } from "../utils/apiUrl";
import { auth } from "../utils/auth";
import { toast } from "../utils/helpers";

/**
 * @param {object} opciones - Opcional. Sin este argumento el comportamiento es
 *   idéntico al de siempre (retorna el cuerpo parseado, o undefined con un toast
 *   ante error de red o cuerpo no-JSON).
 * @param {object} opciones.headers - Cabeceras extra (ej. Idempotency-Key).
 * @param {boolean} opciones.raw - Retorna la forma normalizada
 *   { ok, status, headers, body, error, tipoError } en vez del cuerpo pelado.
 *   status === 0 significa que el request nunca llegó al servidor.
 *   Implica no mostrar toasts: el llamador es dueño de los mensajes.
 * @param {AbortSignal} opciones.signal
 * @param {boolean} opciones.silencioso - No mostrar el toast de error genérico.
 */
export const apiRequest = async (data, url, method = "post", opciones = {}) => {
	const {
		headers: headersExtra = {},
		raw = false,
		signal,
		silencioso = false,
	} = opciones;

	const headers = {
		Accept: "application/json",
		"X-Request-With": "XMLHttpRequest",
		Authorization: "Bearer " + auth.getToken(),
		"X-Scopes": auth.getUser().rol,
	};

	// fetch lanza TypeError si una cabecera trae undefined/null
	Object.keys(headersExtra).forEach((clave) => {
		if (headersExtra[clave] !== undefined && headersExtra[clave] !== null) {
			headers[clave] = headersExtra[clave];
		}
	});

	const metodo = method.toLocaleLowerCase();

	let options = {};

	if (
		metodo === "post" ||
		metodo === "delete" ||
		metodo === "put" ||
		metodo === "patch"
	) {
		options = {
			method,
			body: data,
			headers,
		};
	}

	if (metodo === "get") {
		options = {
			headers,
		};
	}

	if (signal) {
		options.signal = signal;
	}

	let res;

	try {
		res = await fetch(apiUrl(url), options);
	} catch (error) {
		// Nunca hubo respuesta: red caída, CORS, DNS, o abort.
		const tipoError = error && error.name === "AbortError" ? "abortado" : "red";

		if (raw) {
			return { ok: false, status: 0, headers: null, body: null, error, tipoError };
		}

		if (!silencioso) toast("Error Procesando consulta", "error");
		return undefined;
	}

	// El servidor respondió; el cuerpo puede no ser JSON (ej. un 502 con HTML).
	let body = null;
	let errorParseo = null;

	try {
		body = await res.json();
	} catch (error) {
		errorParseo = error;
	}

	if (raw) {
		return {
			ok: res.ok,
			status: res.status,
			headers: res.headers,
			body,
			error: errorParseo,
			tipoError: errorParseo ? "parseo" : null,
		};
	}

	if (errorParseo) {
		if (!silencioso) toast("Error Procesando consulta", "error");
		return undefined;
	}

	return body;
};
